import { useMemo } from 'react'

import { Button, Message } from '~/components/component-library'
import { useSwapQuoteForm } from '~/components/forms/swap/hooks/useSwapQuoteForm'
import { useTransferQuote } from '~/components/forms/transfer/hooks/useTransferQuote'
import { useTokenPrices } from '~/hooks/useTokenPrices'
import { displayCurrencyShorthand } from '~/utils/display'

// If the savings are greater than this amount, we show a warning
const MIN_PRICE_IMPACT_AMOUNT = 10

/**
 * This component displays a recommendation to do a "reverse swap" (exact-output to exact-input) if the price impact is greater than the tolerance amount
 */
export const ReverseSwapSuggestionMessage = ({
  isLoadingQuote,
  isPrivate,
}: {
  isLoadingQuote?: boolean
  isPrivate?: boolean
}) => {
  const {
    reverseQuote,
    reverseSwapQuote,
    isDirectionReversed: isReversed,
  } = useTransferQuote({ isPrivate })

  const swapQuote = useSwapQuoteForm()
  const { convertTokenToUSD } = useTokenPrices()

  const savingsUSD = useMemo(() => {
    if (!swapQuote || !reverseSwapQuote?.data || isLoadingQuote) {
      return null
    }

    try {
      const sellAmountUSD = convertTokenToUSD({
        token: swapQuote.sellToken,
        amount: swapQuote.sellAmount,
        chainId: swapQuote.chainId,
      })

      const reverseSellAmountUSD = convertTokenToUSD({
        token: swapQuote.sellToken,
        amount: reverseSwapQuote.data.sellAmount,
        chainId: swapQuote.chainId,
      })

      if (
        sellAmountUSD === undefined ||
        reverseSellAmountUSD === undefined ||
        reverseSellAmountUSD === 0
      ) {
        return null
      }

      return sellAmountUSD - reverseSellAmountUSD
    } catch {
      return null
    }
  }, [swapQuote, reverseSwapQuote, convertTokenToUSD, isLoadingQuote])

  if (savingsUSD === null) {
    return null
  }

  const usdText = displayCurrencyShorthand(savingsUSD)

  if (isReversed) {
    // When reversed, swapQuote reflects the reversed (SELL) quote,
    // so savingsUSD becomes negative. We flip the sign to counteract this
    return (
      <Message
        variant="success"
        title="Received amount will be approximated"
        action={
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={reverseQuote}
          >
            Undo
          </Button>
        }
      >
        {`You're saving ${displayCurrencyShorthand(savingsUSD * -1)} on this swap with an optimized route`}
      </Message>
    )
  }

  if (savingsUSD <= MIN_PRICE_IMPACT_AMOUNT) {
    return null
  }

  return (
    <Message
      variant="info"
      title="Don't need an exact amount?"
      action={
        <Button
          type="button"
          variant="primary"
          size="small"
          onClick={reverseQuote}
        >
          Save {usdText}
        </Button>
      }
    >
      {`Save ${displayCurrencyShorthand(savingsUSD)} on this swap with an approximate output`}
    </Message>
  )
}
